import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getUfDiaria, updateUfDiaria, deleteUfDiaria, syncUfFromBancoCentral } from "../../services/ufDiariaService";
import { Button } from "../../components/ui/button";
import { Switch, RadioPills } from "../../components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { YoolTable, StatusPill, MonoText, RowActions, IconButton } from "../../components/ui/YoolTable";
import { PlusCircle, RefreshCw } from "lucide-react";

export default function UfDiariaList() {
  const navigate = useNavigate();
  const [ufDiaria, setUfDiaria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ufToDelete, setUfToDelete] = useState(null);
  const [filter, setFilter] = useState("todos");

  useEffect(() => {
    loadUfDiaria();
  }, []);

  const loadUfDiaria = async () => {
    try {
      setLoading(true);
      const data = await getUfDiaria();
      // Ordenar por fecha descendente (más reciente primero)
      const sortedData = data.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      setUfDiaria(sortedData);
    } catch (error) {
      console.error("Error loading UF diaria:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (uf, newStatus) => {
    try {
      // Usar el ID correcto (id en lugar de recordId)
      await updateUfDiaria(uf.id, {
        status: newStatus ? 'active' : 'inactive'
      });
      
      // Update local state usando id
      setUfDiaria(ufDiaria.map(u => 
        u.id === uf.id 
          ? { ...u, status: newStatus ? 'active' : 'inactive' } 
          : u
      ));
    } catch (error) {
      console.error("Error updating UF diaria status:", error);
    }
  };

  const handleDeleteClick = (uf) => {
    setUfToDelete(uf);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!ufToDelete) return;
    
    try {
      await deleteUfDiaria(ufToDelete.id);
      setUfDiaria(ufDiaria.filter(u => u.id !== ufToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Error deleting UF diaria:", error);
    }
  };

  const handleSyncBancoCentral = async () => {
    try {
      setSyncing(true);
      
      // Llamar sin parámetros para que sincronice automáticamente desde la última fecha
      const result = await syncUfFromBancoCentral();
      await loadUfDiaria(); // Recargar datos después de la sincronización
      
      const message = result.message 
        ? `${result.message}` 
        : `Sincronización exitosa desde ${result.source}. Registros procesados: ${result.recordsProcessed}, insertados: ${result.recordsInserted}`;
      
      alert(message);
    } catch (error) {
      console.error("Error syncing with CMF API:", error);
      alert("Error al sincronizar: " + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Crear fecha en UTC para evitar problemas de zona horaria
    const date = new Date(dateString + 'T00:00:00.000Z');
    return date.toLocaleDateString('es-CL', {
      timeZone: 'UTC',
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return 'N/A';
    // Convertir string a número si es necesario
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 2
    }).format(numValue);
  };

  const displayRows = filter === "activos"
    ? ufDiaria.filter(uf => uf.status === 'active')
    : filter === "inactivos"
      ? ufDiaria.filter(uf => uf.status === 'inactive')
      : ufDiaria;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--yool-black)' }}>UF Diaria</h1>
          <p style={{ color: 'var(--yool-muted)' }}>Gestión de valores de Unidad de Fomento</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSyncBancoCentral}
            disabled={syncing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando…' : 'Sincronizar CMF'}
          </Button>
          <Link to="/admin/uf-diaria/new">
            <Button style={{ background: 'var(--yool-cta)', color: '#fff' }}>
              <PlusCircle className="h-4 w-4 mr-2" />
              Nueva UF
            </Button>
          </Link>
        </div>
      </div>

      {/* Radio pill del Yool DS: un estado entre varios, todos a la vista.
          El activo iba en `--yool-cta`, el mismo teal del botón "Nueva UF".
          Ver components/ui/switch.jsx. */}
      <RadioPills
        aria-label="Filtrar UF por estado"
        value={filter}
        onChange={setFilter}
        options={[
          { value: 'todos',     label: `Todos (${ufDiaria.length})` },
          { value: 'activos',   label: `Activos (${ufDiaria.filter(u => u.status === 'active').length})` },
          { value: 'inactivos', label: `Inactivos (${ufDiaria.filter(u => u.status === 'inactive').length})` },
        ]}
      />

      <YoolTable
        columns={[
          { key: 'estado', label: 'Estado',
            render: uf => (
              <div className="flex items-center gap-2">
                <Switch checked={uf.status === 'active'} onCheckedChange={checked => handleStatusToggle(uf, checked)} />
                <StatusPill estado={uf.status === 'active' ? 'activo' : 'inactivo'} />
              </div>
            )},
          { key: 'fecha',     label: 'Fecha',    sortable: true,
            sortValue: uf => uf.fecha,
            render: uf => formatDate(uf.fecha) },
          { key: 'valor_clp', label: 'Valor UF', sortable: true, align: 'right', numeric: true,
            sortValue: uf => parseFloat(uf.valor_clp) || 0,
            render: uf => <MonoText style={{ color: 'var(--yool-navy)', fontWeight: 700 }}>{formatCurrency(uf.valor_clp)}</MonoText> },
          { key: 'fuente',    label: 'Fuente',
            render: uf => <MonoText>{uf.fuente || 'Manual'}</MonoText> },
          { key: 'acciones',  label: '', align: 'right',
            render: uf => (
              <RowActions>
                <IconButton icon="edit"  title="Editar"   onClick={() => navigate(`/admin/uf-diaria/${uf.id}`)} />
                <IconButton icon="trash" title="Eliminar" danger onClick={() => handleDeleteClick(uf)} />
              </RowActions>
            )},
        ]}
        rows={loading ? [] : displayRows}
        rowKey="id"
        search={{ placeholder: 'Buscar por fecha, valor o fuente…', keys: ['fuente'] }}
        initialSort={{ key: 'fecha', dir: 'desc' }}
        empty={loading
          ? { icon: 'refresh', title: 'Cargando UF Diaria…', text: '' }
          : { icon: 'search', title: 'Sin registros', text: filter !== 'todos' ? 'No hay registros con el filtro aplicado.' : 'No hay valores de UF disponibles.' }}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente el valor de UF del {formatDate(ufToDelete?.fecha)}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}